import { firebasePasswordSignIn } from '../firebaseAuth';
import { http } from '../lib/api/http';
import { submitSupportTicket } from './supportApi';
import { fetchCurrentUserProfile } from './profileApi';
import { setSessionUser, refreshSessionUser } from './sessionUser';
import {
  buildReferralLink,
  sanitizeReferralCode as sanitizeReferralCodeValue,
} from '../utils/referralAttribution';

const BACKEND_ROLE_BY_PB_ROLE = {
  client: 'customer',
  provider: 'provider',
  admin: 'admin',
};
const DEFAULT_BACKEND_ROLE = BACKEND_ROLE_BY_PB_ROLE.client;

const DEFAULT_PASSWORD_LENGTH = 18;
const PASSWORD_ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';

const CATEGORY_BY_ROLE = {
  client: ['client'],
  provider: ['ground_transport'],
};

const COMPANY_CATEGORY_VALUES = [
  'ground_transport',
  'security_service',
  'suppliers',
  'logistics_operator',
  'client',
  'road_assistance',
  'customs_agent',
  'freight_forwarder',
  'news_agent',
];
const COMPANY_CATEGORY_SET = new Set(COMPANY_CATEGORY_VALUES);

const DEFAULT_COMPANY_CATEGORY = ['suppliers'];

const sanitizeWhitespace = value => value.replace(/\s+/g, ' ').trim();

const normalizeEmail = email => String(email || '').trim().toLowerCase();

const normalizePhoneNumberOrThrow = rawPhone => {
  const value = sanitizeWhitespace(String(rawPhone || ''));
  if (!value) {
    throw new Error(
      "El teléfono es obligatorio y debe incluir el prefijo internacional (ej. +525512345678)."
    );
  }
  const compact = value.replace(/[\s-]/g, '');
  if (!compact.startsWith('+')) {
    throw new Error("El teléfono debe iniciar con '+' seguido del código de país (ej. +52...).");
  }
  const digits = compact.slice(1);
  if (!/^\d+$/.test(digits)) {
    throw new Error("El teléfono solo puede contener dígitos después del '+'.");
  }
  if (digits.length < 8 || digits.length > 15) {
    throw new Error('El teléfono debe tener entre 8 y 15 dígitos después del código de país.');
  }
  return `+${digits}`;
};

const mapRoleToBackend = role => {
  const normalized = String(role || '').toLowerCase();
  return BACKEND_ROLE_BY_PB_ROLE[normalized] || DEFAULT_BACKEND_ROLE;
};

const normalizeCompanyCategories = values => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  const normalized = [];
  for (const item of list) {
    const value = String(item || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!value || !COMPANY_CATEGORY_SET.has(value) || normalized.includes(value)) {
      continue;
    }
    normalized.push(value);
  }
  return normalized;
};

const randomSuffix = () => Math.random().toString(36).slice(2, 6);

const normalizeSlug = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const buildDisplayName = ({ firstName, lastName, company, email }) => {
  const parts = [firstName, lastName].map(part => sanitizeWhitespace(part || ''));
  const fullName = parts.filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  const cleanCompany = sanitizeWhitespace(company || '');
  if (cleanCompany) return cleanCompany;
  return email;
};

const generatePassword = (length = DEFAULT_PASSWORD_LENGTH) => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);
    return Array.from(randomValues, value => PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length]).join(
      ''
    );
  }
  let password = '';
  for (let index = 0; index < length; index += 1) {
    const randomIndex = Math.floor(Math.random() * PASSWORD_ALPHABET.length);
    password += PASSWORD_ALPHABET[randomIndex];
  }
  return password;
};

const normalizeCompanyCategoriesByRole = role => CATEGORY_BY_ROLE[role] || DEFAULT_COMPANY_CATEGORY;

const normalizeReferralLink = (link, code) => {
  const normalizedCode = sanitizeReferralCodeValue(code);
  if (!normalizedCode) {
    return '';
  }
  if (link) {
    return link;
  }
  return buildReferralLink(normalizedCode);
};

const dispatchCreditsUpdated = record => {
  if (typeof window === 'undefined' || !record) {
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent('flotte:credits-updated', { detail: record }));
  } catch {
    // ignore event errors
  }
};

const extractErrorMessage = error => {
  const detail = error?.response?.data?.detail;
  if (detail) {
    return detail;
  }
  const message = error?.response?.data?.message;
  if (message) {
    return message;
  }
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors[0]?.message || 'No se pudo completar el registro.';
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return 'No se pudo completar el registro. Inténtalo de nuevo.';
};

const ensureUniqueCompanySlug = async originalName => {
  const normalizedBase = normalizeSlug(originalName) || `empresa-${randomSuffix()}`;
  let candidate = normalizedBase;

  for (let attempt = 0; attempt < 6; attempt += 1) {
    if (attempt > 0) {
      candidate = `${normalizedBase}-${randomSuffix()}`;
    }

    try {
      await http.get(`/companies/${encodeURIComponent(candidate)}`);
      continue;
    } catch (error) {
      if (error?.status === 404) {
        return candidate;
      }
      if (attempt === 5) {
        throw error;
      }
    }
  }

  return `${normalizedBase}-${Date.now().toString(36)}`;
};

const linkUserToCompany = async (userId, companyId) => {
  if (!userId || !companyId) {
    return;
  }
  try {
    await http.patch(`/users/${encodeURIComponent(userId)}`, {
      company_id: companyId,
    });
  } catch (error) {
    console.warn('No se pudo actualizar la empresa del usuario.', error);
  }
};

async function createCompanyDraftForUser({
  creatorId,
  ownerId,
  originalName,
  role,
  categories,
  taxId,
}) {
  const name = sanitizeWhitespace(originalName || '');
  if (!name || !creatorId) {
    return null;
  }

  const baseCategories =
    Array.isArray(categories) && categories.length > 0
      ? categories
      : normalizeCompanyCategoriesByRole(role);

  const slug = await ensureUniqueCompanySlug(name);

  const payload = {
    name,
    slug,
    status: 'draft',
    membership_plan: 'free',
    can_publish: false,
    is_verified: false,
    owner_id: ownerId || creatorId,
    categories: baseCategories,
    rfc: sanitizeWhitespace(taxId || ''),
  };

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await http.post('/companies', payload);
      const company = response.data || {};
      await linkUserToCompany(ownerId || creatorId, company.id);
      await refreshSessionUser().catch(() => {});
      return company;
    } catch (error) {
      if (error?.status === 409 && error?.response?.data?.detail?.includes('slug')) {
        payload.slug = `${slug}-${randomSuffix()}`;
        continue;
      }
      throw error;
    }
  }

  throw new Error('No se pudo registrar la empresa. Intenta nuevamente o contacta a soporte.');
}

async function updateUserCompanyName(userId, companyName) {
  if (!userId) return;
  try {
    await http.patch(`/users/${encodeURIComponent(userId)}`, {
      company_name: sanitizeWhitespace(companyName || ''),
    });
    await refreshSessionUser().catch(() => {});
  } catch (error) {
    console.warn('No se pudo guardar el nombre de la empresa del cliente.', error);
  }
}

async function assignCompanyOwner(companyId, ownerId) {
  if (!companyId || !ownerId) {
    return;
  }
  try {
    await http.patch(`/companies/${encodeURIComponent(companyId)}`, {
      owner_id: ownerId,
    });
    await linkUserToCompany(ownerId, companyId);
    await refreshSessionUser().catch(() => {});
  } catch (error) {
    if (error?.status === 403) {
      throw new Error('No tienes permisos para reclamar esta empresa.');
    }
    throw error;
  }
}

async function createCompanyAccessRequest({
  companyId,
  requesterId,
  requesterName,
  requesterEmail,
  role,
}) {
  if (!companyId || !requesterId) {
    return;
  }

  const displayName = requesterName || requesterEmail || 'Usuario';
  const roleLabel =
    role === 'provider' ? 'proveedor' : role === 'client' ? 'cliente' : 'usuario';
  const subject = `Solicitud de acceso de ${displayName}`;
  const lines = [
    `ID usuario: ${requesterId}`,
    `Correo: ${requesterEmail || 'sin correo'}`,
    `Rol solicitado: ${roleLabel}`,
    '',
    `El ${roleLabel} ${displayName} quiere unirse a tu empresa en Flotte.`,
    '',
    'Acciones sugeridas:',
    '1. Verifica que el usuario pertenezca a tu organización.',
    '2. Asigna la empresa al usuario desde el panel de administración o solicita ayuda a soporte.',
    '3. Marca esta solicitud como atendida cuando termines.',
  ];
  const description = lines.join('\n');

  await submitSupportTicket({
    subject,
    description,
    companyId,
    reporterId: requesterId,
    ticketType: 'support',
    category: 'general',
    priority: 'high',
    reporterName: requesterName,
    reporterEmail: requesterEmail,
  });
}

async function redeemReferralCode({ referralCode, referralLink }) {
  const sanitizedCode = sanitizeReferralCodeValue(referralCode);
  if (!sanitizedCode) {
    return;
  }

  try {
    const body = {
      referral_code: sanitizedCode,
      referral_link: referralLink || '',
    };
    await http.post('/referrals/redeem', body);
    await refreshSessionUser().catch(() => {});
  } catch (error) {
    if (error?.status === 409) {
      console.warn('El usuario ya había canjeado un código de referido.');
      return;
    }
    if (error?.status === 404) {
      console.warn('El código de referido enviado no es válido.');
      return;
    }
    console.warn('No se pudo aplicar la recompensa de referido.', error);
  }
}

export async function registerUserAccount({
  role,
  firstName,
  lastName,
  email,
  phone,
  company,
  password,
  existingCompanyId,
  companyCategory,
  companyTaxId = '',
  isCompanyOwner = false,
  referralCode = '',
  referralLink = '',
}) {
  if (!role) {
    throw new Error('No se especificó el tipo de cuenta a registrar.');
  }

  const isClientRole = role === 'client';
  const normalizedEmail = normalizeEmail(email || '');
  if (!normalizedEmail) {
    throw new Error('El correo electrónico es obligatorio.');
  }

  const displayName = buildDisplayName({
    firstName,
    lastName,
    company,
    email: normalizedEmail,
  });

  const normalizedPhone = normalizePhoneNumberOrThrow(phone);
  const normalizedCompany = sanitizeWhitespace(company || '');
  const normalizedExistingCompanyId = sanitizeWhitespace(existingCompanyId || '');
  const normalizedCompanyCategories = normalizeCompanyCategories(companyCategory);
  const normalizedCompanyTaxId = sanitizeWhitespace(companyTaxId || '');
  const userPassword = typeof password === 'string' ? password : '';
  const trimmedPassword = userPassword.trim();
  const passwordToUse = trimmedPassword || generatePassword();
  const sanitizedReferralCodeInput = sanitizeReferralCodeValue(referralCode);
  const normalizedReferralLink = normalizeReferralLink(referralLink, sanitizedReferralCodeInput);

  try {
    const backendRole = mapRoleToBackend(role);
    await http.post('/v1/users', {
      email: normalizedEmail,
      password: passwordToUse,
      display_name: displayName,
      phone_number: normalizedPhone,
      role: backendRole,
    });

    await firebasePasswordSignIn(normalizedEmail, passwordToUse);
    const currentProfile = await fetchCurrentUserProfile();
    setSessionUser(currentProfile);
    const currentUserId = currentProfile?.id;
    if (!currentUserId) {
      throw new Error('No pudimos autenticar la sesión recién creada.');
    }

    if (normalizedExistingCompanyId) {
      if (isCompanyOwner) {
        try {
          await assignCompanyOwner(normalizedExistingCompanyId, currentUserId);
        } catch (ownershipError) {
          console.warn('No se pudo asignar la propiedad de la empresa.', ownershipError);
          await createCompanyAccessRequest({
            companyId: normalizedExistingCompanyId,
            requesterId: currentUserId,
            requesterName: displayName,
            requesterEmail: normalizedEmail,
            role,
          });
        }
      } else {
        await createCompanyAccessRequest({
          companyId: normalizedExistingCompanyId,
          requesterId: currentUserId,
          requesterName: displayName,
          requesterEmail: normalizedEmail,
          role,
        });
      }
    } else if (normalizedCompany && !isClientRole) {
      await createCompanyDraftForUser({
        creatorId: currentUserId,
        ownerId: isCompanyOwner ? currentUserId : '',
        originalName: normalizedCompany,
        role,
        categories: normalizedCompanyCategories.length
          ? normalizedCompanyCategories
          : normalizeCompanyCategoriesByRole(role),
        taxId: normalizedCompanyTaxId,
      });
    }

    if (isClientRole) {
      await updateUserCompanyName(currentUserId, normalizedCompany);
    }

    if (sanitizedReferralCodeInput) {
      await redeemReferralCode({
        referralCode: sanitizedReferralCodeInput,
        referralLink: normalizedReferralLink,
      });
      try {
        const refreshedProfile = await fetchCurrentUserProfile();
        dispatchCreditsUpdated(refreshedProfile);
      } catch (refreshError) {
        console.warn('No se pudo refrescar el perfil después de aplicar el referido.', refreshError);
      }
    }

    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(
          'flotte:last-registration',
          JSON.stringify({
            role,
            company: normalizedCompany,
            existingCompanyId: normalizedExistingCompanyId,
            companyCategory: normalizedCompanyCategories[0] || '',
            companyTaxId: normalizedCompanyTaxId,
            firstName: sanitizeWhitespace(firstName || ''),
            lastName: sanitizeWhitespace(lastName || ''),
            email: normalizedEmail,
            passwordWasProvided: Boolean(trimmedPassword),
            isCompanyOwner,
            registeredAt: Date.now(),
            referralCode: sanitizedReferralCodeInput,
            referralLink: normalizedReferralLink,
          })
        );
      } catch {
        // La sesión puede no estar disponible (modo privado). Ignorar.
      }
    }

    return currentProfile;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}
